import { LanguagesEnum } from '@/api/enums';
import {
  ModifiedAnswerOption,
  ModifiedQuestion,
} from '@/client/quizzes/types/quizzes.type';
import { getTranslatedContent } from '@/utils/helpers/quizzes.helper';
import {
  getSecurityProfileAnswerOptionKey,
  getSecurityProfileQuestionKey,
} from '@/utils/helpers/security-profile.helper';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export const useSecurityProfileText = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.language as LanguagesEnum;

  const getQuestionText = useCallback(
    (question: ModifiedQuestion | undefined): string => {
      const questionKey = getSecurityProfileQuestionKey(question);

      return questionKey
        ? t(`${questionKey}.text`)
        : getTranslatedContent(question, language);
    },
    [t, language],
  );

  const getAnswerOptionText = useCallback(
    (
      question: ModifiedQuestion | undefined,
      answerOption: ModifiedAnswerOption | undefined,
    ): string => {
      const answerOptionKey = getSecurityProfileAnswerOptionKey(
        question,
        answerOption,
      );

      return answerOptionKey
        ? t(answerOptionKey)
        : getTranslatedContent(answerOption, language);
    },
    [t, language],
  );

  const getAnswerOptionPlaceholder = useCallback(
    (
      question: ModifiedQuestion | undefined,
      answerOption: ModifiedAnswerOption | undefined,
    ): string => {
      const answerOptionKey = getSecurityProfileAnswerOptionKey(
        question,
        answerOption,
      );

      return answerOptionKey
        ? t(`${answerOptionKey}.placeholder`)
        : getTranslatedContent(answerOption, language, 'placeholderText');
    },
    [t, language],
  );

  const getAnswerOptionTooltip = useCallback(
    (
      question: ModifiedQuestion | undefined,
      answerOption: ModifiedAnswerOption | undefined,
    ): string => {
      const answerOptionKey = getSecurityProfileAnswerOptionKey(
        question,
        answerOption,
      );

      if (!answerOptionKey) {
        return getTranslatedContent(answerOption, language, 'tooltipText');
      }

      const tooltipKey = `${answerOptionKey}.tooltip`;

      return i18n.exists(tooltipKey) ? t(tooltipKey) : '';
    },
    [t, i18n, language],
  );

  return {
    getQuestionText,
    getAnswerOptionText,
    getAnswerOptionPlaceholder,
    getAnswerOptionTooltip,
  };
};
